<script lang="ts">
  // components
  import Photos from './Photos.svelte';

  // utils
  import { intcomma } from 'journalize';
  import { formatPercent } from '../utils/format.js';
  import { slugify } from '../utils/slugify.js';
  import {
    choiceName,
    isMeasure,
    isRunoff,
    isWinner,
    measureLabel,
    nameParts,
    orderedChoices,
    partyClass,
  } from '../utils/race-helpers.js';

  // types
  import type { Readable } from 'svelte/store';
  import type { Choice, CrosswalkImage, Race } from '../types';

  /** The race's own file (RACE_URL), from mountHpEmbed */
  export let race: Readable<Race>;
  /** window.CROSSWALK.assets.img, keyed by choiceId (the AP candidate id) */
  export let crosswalk: Record<string, CrosswalkImage>;
  /** Display identifier from the embed's JS file, e.g. 'hp-gov' */
  export let name: string;
  export let slug = '';

  $: totalVotes = $race.attributes.totalVotes;
  $: measure = isMeasure($race) || name === 'measure';
  $: choices = orderedChoices($race);
  // one legend line no matter how many incumbents (e.g. CD 40 has two)
  $: hasIncumbent = choices.some((c) => c.isIncumbent);

  // race ids that show headshots
  const facelist: string[] = [];
  $: face = facelist.includes($race.id);

  $: alwaysShowCandidates = slug !== 'hp-senate-class-i' ? 2 : 3;

  /** Party label, hidden for measures and nonpartisan races ('NP' or no party) */
  function partyLabel(c: Choice) {
    if (measure) return '';
    const abbr = c.party?.abbr;
    return abbr && abbr !== 'NP' ? abbr : '';
  }
</script>

{#if hasIncumbent}
  <p class="winner-incumbent text-sans-serif mt-0 text-left"><span>* Incumbent</span></p>
{/if}
<table class="contest__candidates">
  <thead>
    <tr>
      <th  scope="col" colspan="1" class="contest__candidate-header">
      </th>
      <th scope="col" colspan={face? 2:1}  class="contest__candidate-header text-uppercase">
        {#if measure}Support{:else}Candidate{/if}
      </th>
      <th scope="col" class="contest__candidate-header text-left text-uppercase">
       Votes
      </th>
      <th scope="col" class="contest__candidate-header text-uppercase total-votes">
        Total
      </th>
    </tr>
  </thead>
  <tbody>
    {#each choices as c, idx}
      {#if idx < alwaysShowCandidates}
        {@const { first, middle, last } = nameParts(c)}
        <tr class="candidate contest__candidate-row">
          <td class="candidate__cell candidate__cell-bar">
            <div
            class="bar bar-{partyClass(c)}"
          >
            {#if isWinner(c)}
              <div class="candidate__cell-winner" aria-label="checkmark, winner"> ✓ </div>
            {/if}
          </div>
          </td>
          {#if face && crosswalk[c.choiceId]}
          <td class="candidate__cell">
            <div class="candidate__cell-headshot {slugify(choiceName(c))}">
              <Photos
                name = {choiceName(c)}
                src = {crosswalk[c.choiceId].png}
                avif = {crosswalk[c.choiceId].avif}
                webp = {crosswalk[c.choiceId].webp}
                party= None
              />
            </div>
            </td>
            {/if}
          <th
            scope="row"
            class="candidate__cell candidate__cell-name"
          >
          <!-- measures have no name parts, only 'Yes' / 'No' (see measureLabel) -->
          {#if measure}{measureLabel(c)}{:else}{first} {#if middle}{middle} {/if}{last}{/if}&nbsp;{#if c.isIncumbent}<span
                class="candidate__cell-incumbent"
                title="Currently holds this office">*</span
              >
          {/if}
          {#if partyLabel(c)}
          <span class="candidate__cell-name-party">({partyLabel(c)})</span>
          {/if}
          {#if isRunoff(c)}
                <span class="candidate__cell-runoff candidate__">Runoff</span>
          {/if}
          </th>
          <td
            class="candidate__cell candidate__cell-pct text-left">
            {formatPercent(c.votes, totalVotes, 1)}<span class="candidate__pct-symbol">%</span>
          </td>
          <td
            class="candidate__cell candidate__cell-pct text-right total-votes">
            {intcomma(c.votes)}
          </td>
        </tr>
      {/if}
    {/each}
  </tbody>
  {#if $race.attributes.eevp != null && ($race.attributes.eevp > 0 || totalVotes === 0)}
  <tfoot>
    <tr>
      <td colspan="4" class="contest__precincts text-left" data-component-field="hp-embed-eevp">
          {$race.attributes.eevp}% of expected vote in
      </td>
    </tr>
  </tfoot>
  {/if}
</table>
